"use client";

import { Controller, useFormContext, UseFormReturn } from "react-hook-form";
import { RouterOutputs } from "~/utils/api";

import { Unpacked } from "~/types/helpers";

import { RSVPStepsFormInputData } from "../validators";
import { useEffect } from "react";
import { Label } from "~/components/v3/shadcn/Label";
import { Input } from "~/components/v3/themed/Input";
import { MultiSelect } from "~/components/shadcn/MultiSelect";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/shadcn/Select";
import { linkedInSchema, urlSchema } from "~/types/EventTypes";
import { DefaultUserValues } from "../actions/rsvp-steps-and-data";
import { CustomizableLayout } from "~/types/CustomizableLayout";
import { cn } from "~/utils/fontsConfig";
import { useWhiteLabel } from "~/app/providers";

type QuestionnaireOutput = Unpacked<RouterOutputs["questionnaire"]["getAll"]>;

export type QuestionnaireEssential = Pick<
    QuestionnaireOutput,
    "id" | "name" | "questionsv2"
>;

type Props = {
    questionnaires?: QuestionnaireEssential[] | null;
    setNextDisabled: (disabled: boolean) => void;
    defaultUserValues: DefaultUserValues;
};

const QuestionnaireModalContent = ({
    questionnaires,
    setNextDisabled,
    defaultUserValues,
}: Props) => {
    const form = useFormContext<RSVPStepsFormInputData>();
    const { formState, resetField } = form;
    const whitelabel = useWhiteLabel();
    const customization = whitelabel?.customization;

    useEffect(() => {
        questionnaires?.forEach((questionnaire, qIndex) => {
            questionnaire.questionsv2.forEach((question, index) => {
                Object.entries(defaultUserValues).forEach(([key, value]) => {
                    if (!question.question.includes(key)) return;
                    question.question = question.question.replace(key, "");
                    value &&
                        resetField(
                            `questionnaires.${qIndex}.answersv2.${index}.text`,
                            {
                                defaultValue: value,
                            },
                        );
                });
            });
        });
    }, [questionnaires, resetField, defaultUserValues]);

    useEffect(() => {
        if (!questionnaires) return;
        setNextDisabled(!formState.isValid);
    }, [
        formState.isValid,
        formState.dirtyFields,
        setNextDisabled,
        questionnaires,
    ]);

    if (!questionnaires) {
        return null;
    }

    return (
        <>
            {questionnaires.map((questionnaire, qIndex) => {
                return (
                    <div
                        key={questionnaire.id}
                        className="flex flex-col justify-start gap-4 text-start"
                    >
                        <div className="flex flex-col gap-2 border-b border-v3-N400 pb-4 text-left text-[16px]">
                            <Label
                                className={cn(
                                    "mt-4 text-white",
                                    customization?.labelClassName,
                                )}
                            >
                                {questionnaire.name}
                            </Label>
                            <div className="flex flex-col justify-start text-start">
                                {questionnaire.questionsv2.map(
                                    (_, fieldIndex) => (
                                        <QuestionnaireQuestion
                                            key={fieldIndex}
                                            fieldIndex={fieldIndex}
                                            qIndex={qIndex}
                                            questionnaire={questionnaire}
                                            customization={customization}
                                            form={form}
                                        />
                                    ),
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </>
    );
};

const QuestionnaireQuestion = ({
    fieldIndex,
    qIndex,
    form,
    questionnaire,
    customization,
}: {
    fieldIndex: number;
    qIndex: number;
    form: UseFormReturn<RSVPStepsFormInputData>;
    questionnaire: QuestionnaireEssential;
    customization?: CustomizableLayout;
}) => {
    return (
        <div className="mt-6 text-left font-mono text-[16px] font-normal leading-normal">
            <div>
                <Label
                    htmlFor={`questionnaires-${qIndex}-answersv2-${fieldIndex}`}
                    className={cn(
                        "text-v3-N300",
                        customization?.labelClassName,
                    )}
                >
                    {questionnaire.questionsv2[fieldIndex]?.question}
                </Label>
                {questionnaire.questionsv2[fieldIndex]?.isRequired && (
                    <span className="ml-1 align-top text-xs text-red-500">
                        *
                    </span>
                )}
            </div>
            {questionnaire.questionsv2[fieldIndex]?.description && (
                <Label
                    htmlFor={`questionnaires-${qIndex}-answersv2-${fieldIndex}`}
                    className={cn(
                        "text-sm text-v3-N400",
                        customization?.descriptionClassName,
                    )}
                >
                    {questionnaire.questionsv2[fieldIndex]?.description}
                </Label>
            )}
            {questionnaire.questionsv2[fieldIndex]?.type === "text" && (
                <div className="flex space-x-2">
                    <Input
                        {...form.register(
                            `questionnaires.${qIndex}.answersv2.${fieldIndex}.text`,
                            {
                                required:
                                    questionnaire.questionsv2[fieldIndex]
                                        ?.isRequired,
                            },
                        )}
                        onBlur={() => {
                            form.trigger(
                                `questionnaires.${qIndex}.answersv2.${fieldIndex}.text`,
                            );
                        }}
                        id={`questionnaires-${qIndex}-answersv2-${fieldIndex}`}
                        type="text"
                        className={cn(customization?.inputClassName)}
                    />
                </div>
            )}
            {questionnaire.questionsv2[fieldIndex]?.type === "text-url" && (
                <div className="flex space-x-2">
                    <Input
                        {...form.register(
                            `questionnaires.${qIndex}.answersv2.${fieldIndex}.text`,
                            {
                                required:
                                    questionnaire.questionsv2[fieldIndex]
                                        ?.isRequired,
                                validate: (val) => {
                                    const result = urlSchema.safeParse(val);
                                    return (
                                        result.success ||
                                        result.error?.issues[0]?.message
                                    );
                                },
                            },
                        )}
                        onBlur={() => {
                            form.trigger(
                                `questionnaires.${qIndex}.answersv2.${fieldIndex}.text`,
                            );
                        }}
                        id={`questionnaires-${qIndex}-answersv2-${fieldIndex}`}
                        type="text"
                        className={cn(customization?.inputClassName)}
                    />
                </div>
            )}
            {questionnaire.questionsv2[fieldIndex]?.type ===
                "text-linkedin-url" && (
                <div className="flex space-x-2">
                    <Input
                        {...form.register(
                            `questionnaires.${qIndex}.answersv2.${fieldIndex}.text`,
                            {
                                required:
                                    questionnaire.questionsv2[fieldIndex]
                                        ?.isRequired,
                                validate: (val) => {
                                    const result =
                                        linkedInSchema.safeParse(val);
                                    return (
                                        result.success ||
                                        result.error?.issues[0]?.message
                                    );
                                },
                            },
                        )}
                        onBlur={() => {
                            form.trigger(
                                `questionnaires.${qIndex}.answersv2.${fieldIndex}.text`,
                            );
                        }}
                        id={`questionnaires-${qIndex}-answersv2-${fieldIndex}`}
                        type="text"
                        className={cn(customization?.inputClassName)}
                    />
                </div>
            )}
            {questionnaire.questionsv2[fieldIndex]?.type === "select" && (
                <div className="flex space-x-2">
                    <Controller
                        control={form.control}
                        name={`questionnaires.${qIndex}.answersv2.${fieldIndex}.text`}
                        rules={{
                            required: questionnaire.questionsv2[fieldIndex]
                                ?.isRequired
                                ? "This field is required"
                                : false,
                            validate: (value) =>
                                questionnaire.questionsv2[fieldIndex]
                                    ?.isRequired &&
                                (!value || value.length === 0)
                                    ? "At least one option must be selected"
                                    : true,
                        }}
                        render={({ field }) => (
                            <MultiSelect
                                className="items-center"
                                options={(
                                    questionnaire.questionsv2[fieldIndex]
                                        ?.selectValues ?? []
                                ).map((o) => ({
                                    label: o,
                                    value: o,
                                }))}
                                onChange={(e) => {
                                    field.onChange(e.join(","));
                                    form.trigger(
                                        `questionnaires.${qIndex}.answersv2.${fieldIndex}.text`,
                                    );
                                }}
                            />
                        )}
                    />
                </div>
            )}
            {questionnaire.questionsv2[fieldIndex]?.type ===
                "single-select" && (
                <div className="flex space-x-2">
                    <Controller
                        control={form.control}
                        name={`questionnaires.${qIndex}.answersv2.${fieldIndex}.text`}
                        rules={{
                            required: questionnaire.questionsv2[fieldIndex]
                                ?.isRequired
                                ? "This field is required"
                                : false,
                        }}
                        render={({ field }) => (
                            <Select
                                onValueChange={(val) => {
                                    field.onChange(val);
                                    form.trigger(
                                        `questionnaires.${qIndex}.answersv2.${fieldIndex}.text`,
                                    );
                                }}
                            >
                                <SelectTrigger
                                    className={cn(
                                        "cursor-pointer rounded-md bg-v3-neutral/90 px-4 py-2",
                                        customization?.inputClassName,
                                    )}
                                >
                                    <SelectValue
                                        placeholder={"Choose an option"}
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    {questionnaire.questionsv2[
                                        fieldIndex
                                    ]?.selectValues?.map((o) => (
                                        <SelectItem
                                            key={o}
                                            value={o}
                                            className="flex items-center gap-2"
                                        >
                                            <span>{o}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    />
                </div>
            )}
            {!!form.formState?.errors?.questionnaires &&
                form.formState?.errors?.questionnaires[qIndex]?.answersv2?.[
                    fieldIndex
                ] && (
                    <span className="text-sm text-red-500">
                        {form.formState.errors.questionnaires[qIndex]
                            .answersv2?.[fieldIndex]?.text?.message ||
                            "This field is required"}
                    </span>
                )}
        </div>
    );
};

export default QuestionnaireModalContent;
